import { type MouseEvent, useEffect, useState } from 'react';
import { ConfigProvider } from 'shared/config/ConfigContext';
import { DeviceTypeProvider } from 'shared/contexts/DeviceTypeProvider';
import { t } from 'shared/locale/texts';
import { Head } from 'shared/page/Head';
import { HeroBanner } from 'shared/page/HeroBanner';
import { Main } from 'shared/page/Main';
import { Menu } from 'shared/page/Menu';
import { CarSearch } from 'shared/search/car/CarSearch';
import { SearchVerticalConfig, type Vertical } from 'shared/search/common/config';
import { trackBasicPageView } from 'shared/search/common/tracking';
import { FhhSearch } from 'shared/search/fhh/FhhSearch';
import { FlightSearch } from 'shared/search/flight/FlightSearch';
import { HotelSearch } from 'shared/search/hotel/HotelSearch';
import { PrepackageSearch } from 'shared/search/prepackage/PrepackageSearch';
import {
    appendPrepackageAutocompleteVariant,
    getPrepackageAutocompleteVariant,
    PREPACKAGE_SMART_FILL_EXPERIMENT,
} from 'shared/search/prepackage/utils/geoAutocompleteVariant';
import { updateBannerSubverticalValues } from 'shared/util/bannerParams';
import { ErrorBoundary } from 'shared/util/ErrorBoundary';
import { Hotjar } from 'shared/util/hotjar';
import { CarRentalSale } from '@/components/careRentalSale/CarRentalSale';
import { CheapestDestinations } from '@/components/cheapestDestinations/CheapestDestinations';
import { FrontpageBanner } from '@/components/frontpageBanner/FrontpageBanner';
import { OtherVerticalsNav } from '@/components/otherVerticalsNav/OtherVerticalsNav';
import { FrontpageSeo } from '@/components/seo/FrontpageSeo';
import type { feConfig } from '@/config';
import type { VerticalConfigProps } from '@/config/verticalConfig';
import { verticalConfig } from '@/config/verticalConfig';
import { useExperiment } from '@/hooks/experiments';
import type { SspProps } from '@/util/verticalSsp';
import styles from './Frontpage.module.css';
import { googleBreadcrumb } from './util/googleBreadcrumb';
import { googleFaq } from './util/googleFaq';

type Props = {
    config: VerticalConfigProps;
    feConfig: typeof feConfig;
} & SspProps;

export const Frontpage = ({ config, feConfig, unleash, hasLegalBasis, cheapestDestinations, deviceType }: Props) => {
    const [activeVertical, setActiveVertical] = useState<Vertical>(config.vertical);
    const experiment = useExperiment();
    const activeVerticalConfig = verticalConfig[activeVertical];
    const autocompleteVariant = getPrepackageAutocompleteVariant(experiment?.[PREPACKAGE_SMART_FILL_EXPERIMENT]);

    useEffect(() => {
        trackBasicPageView(activeVertical);
    }, [activeVertical]);

    const pushUrlPath = (clickedVertical: Vertical) => window.history.pushState(null, '', SearchVerticalConfig[clickedVertical].fullPath);

    const setVertical = (event: MouseEvent<HTMLButtonElement>, clickedVertical: Vertical): void => {
        event.preventDefault();
        setActiveVertical(clickedVertical);
        updateBannerSubverticalValues(clickedVertical);
        pushUrlPath(clickedVertical);
    };

    const hotjarEnabled = !!unleash?.hotjar && !!hasLegalBasis;
    const cdLength = cheapestDestinations?.length;

    const showCheapestDestinations = () => activeVertical === 'flight' && cdLength != null && cdLength > 0;

    const showFrontpageBanner = () => !showCheapestDestinations() && activeVertical !== 'fhh';

    return (
        <ErrorBoundary key={`${config.vertical}SearchErrorBoundary`}>
            <ConfigProvider
                config={{
                    ...feConfig,
                    ppAutocompleteUrl: appendPrepackageAutocompleteVariant(feConfig.ppAutocompleteUrl, autocompleteVariant),
                }}>
                <DeviceTypeProvider deviceType={deviceType}>
                    <Head
                        title={t(`htmlMetadata.${activeVertical}.title`)}
                        description={t(`htmlMetadata.${activeVertical}.description`)}
                        canonical={activeVerticalConfig.canonical}>
                        {googleBreadcrumb(activeVertical)}
                        {googleFaq(activeVertical)}
                    </Head>
                    <Main
                        className={styles['frontpage-container']}
                        customContainer
                        broadcastUrl={`/broadcasts?path=https://www.finn.no/reise/fp/${activeVertical}`}>
                        <div className={styles['frontpage-menu-wrapper']}>
                            <Menu activeVertical={activeVertical} setVertical={setVertical} unleash={unleash} />
                        </div>
                        <HeroBanner vertical={activeVertical}>
                            <VerticalSwitch vertical={activeVertical} unleash={unleash} />
                        </HeroBanner>
                        <div className={styles['frontpage-wrapper']}>
                            <h1 className="sr-only" data-testid="travelHeader">
                                {t('travel')}
                            </h1>
                            <div className={styles['advt-container']}>
                                {/*@ts-ignore this custom element is provided by the advertising podlet on runtime */}
                                <advt-component placementId="advt_middle_1" />
                            </div>
                            {showCheapestDestinations() ? <CheapestDestinations destinations={cheapestDestinations} /> : null}
                            {showFrontpageBanner() ? <FrontpageBanner /> : null}
                            <OtherVerticalsNav activeVertical={activeVertical} />
                            {unleash?.carRentalSale ? <CarRentalSale /> : null}
                            <FrontpageSeo vertical={activeVertical} />
                        </div>
                    </Main>
                    <Hotjar enabled={hotjarEnabled} />
                </DeviceTypeProvider>
            </ConfigProvider>
        </ErrorBoundary>
    );
};

type VerticalSwitchProps = {
    vertical: Vertical;
    unleash: Record<string, boolean | string> | null; // cannot be undefined as that is not serializable (toggles are returned from getSSP)
};

const VerticalSwitch = ({ vertical, unleash }: VerticalSwitchProps) => {
    switch (vertical) {
        case 'flight':
            return <FlightSearch position="travel frontpage" />;
        case 'hotel':
            return <HotelSearch position="travel frontpage" />;
        case 'carrental':
            return <CarSearch unleash={unleash} position="travel frontpage" />;
        case 'prepackage':
            return <PrepackageSearch position="travel frontpage" />;
        case 'fhh':
            return <FhhSearch position="travel frontpage" />;
        default:
            return null;
    }
};
